import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { a as additionalQuestionsSchema } from './business-additional-questions-schema.js';
import { F as FormController } from './form.js';
import { A as AdditionalQuestions } from './Business.js';
import { C as CURRENCY_MASK } from './form-input-masks.js';
import { b as businessServiceReceivedOptions, r as recurringPaymentsOptions, s as seasonalBusinessOptions } from './business-form-options.js';
import { d as defineCustomElement$5 } from './form-control-error-text2.js';
import { d as defineCustomElement$4 } from './form-control-help-text2.js';
import { d as defineCustomElement$3 } from './form-control-monetary2.js';
import { d as defineCustomElement$2 } from './form-control-select2.js';
import { d as defineCustomElement$1 } from './form-control-text2.js';

const AdditionalQuestionsFormStepCore = /*@__PURE__*/ proxyCustomElement(class AdditionalQuestionsFormStepCore extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.submitted = createEvent(this, "submitted", 7);
        this.formLoading = createEvent(this, "formLoading", 7);
        this.errorEvent = createEvent(this, "error-event", 7);
        this.getData = () => {
            this.formLoading.emit(true);
            this.getBusiness({
                onSuccess: (response) => {
                    this.additional_questions = new AdditionalQuestions(response.data.additional_questions || {});
                    this.formController.setInitialValues(Object.assign({}, this.additional_questions));
                },
                onError: ({ error, code, severity }) => {
                    this.errorEvent.emit({
                        message: error,
                        errorCode: code,
                        severity: severity
                    });
                },
                final: () => this.formLoading.emit(false)
            });
        };
        this.sendData = (onSuccess) => {
            let submittedData;
            this.formLoading.emit(true);
            this.patchBusiness({
                payload: this.patchPayload,
                onSuccess: (response) => {
                    submittedData = response;
                    onSuccess();
                },
                onError: ({ error, code, severity }) => {
                    submittedData = error;
                    this.errorEvent.emit({
                        message: error,
                        errorCode: code,
                        severity: severity
                    });
                },
                final: () => {
                    this.submitted.emit({ data: { submittedData } });
                    this.formLoading.emit(false);
                }
            });
        };
        this.inputHandler = (name, value) => {
            this.formController.setValues(Object.assign(Object.assign({}, this.formController.values.getValue()), { [name]: value }));
        };
        this.formController = undefined;
        this.errors = {};
        this.additional_questions = {};
        this.getBusiness = undefined;
        this.patchBusiness = undefined;
        this.allowOptionalFields = undefined;
    }
    async validateAndSubmit({ onSuccess }) {
        this.formController.validateAndSubmit(() => this.sendData(onSuccess));
    }
    ;
    get patchPayload() {
        let formValues = new AdditionalQuestions(this.formController.values.getValue()).payload;
        return { additional_questions: formValues };
    }
    componentWillLoad() {
        this.getBusiness && this.getData();
        this.formController = new FormController(additionalQuestionsSchema(this.allowOptionalFields));
    }
    componentDidLoad() {
        this.formController.errors.subscribe(errors => {
            this.errors = Object.assign({}, errors);
        });
    }
    render() {
        var _a, _b, _c, _d, _e, _f, _g;
        const additionalQuestionsDefaultValue = this.formController.getInitialValues();
        return (h("form", { key: '275b193388d360ab6dacfb70ea9e06d68d68cbf8' }, h("fieldset", { key: '740ce0b08ad00efa1345f69cf977e64283307533' }, h("legend", { key: '7f531cdf699ad93234bbdd5d9336ee782e770597' }, "Additional Questions"), h("hr", { key: 'cb81b0b4dc9e6a52ff9bf70571a200fee50e8146' }), h("div", { key: '9100fdb4857a5a9a2b42f0188cfac075974afbeb', class: 'row gy-3' }, h("div", { key: 'ee0885251b8e49cb33359a42897097552e9d0ff9', class: 'col-12 col-md-6' }, h("form-control-monetary", { key: '555b00e0b6728dca26a42813991f35dec41e2577', name: 'business_revenue', label: 'What is the business\u2019 estimated annual revenue from its primary business activities?', inputHandler: this.inputHandler, errorText: (_a = this.errors) === null || _a === void 0 ? void 0 : _a.business_revenue, defaultValue: additionalQuestionsDefaultValue === null || additionalQuestionsDefaultValue === void 0 ? void 0 : additionalQuestionsDefaultValue.business_revenue, maskOptions: CURRENCY_MASK.WHOLE })), h("div", { key: '689ecc46bffe9cfe7190428e191802332485f139', class: 'col-12 col-md-6' }, h("form-control-monetary", { key: '19a4269b31af12f9e07655c9dc0d9db1de46caa2', name: 'business_payment_volume', label: "What is the business' annual credit card & ACH volume anticipated to process?", inputHandler: this.inputHandler, errorText: (_b = this.errors) === null || _b === void 0 ? void 0 : _b.business_payment_volume, defaultValue: additionalQuestionsDefaultValue === null || additionalQuestionsDefaultValue === void 0 ? void 0 : additionalQuestionsDefaultValue.business_payment_volume, maskOptions: CURRENCY_MASK.WHOLE })), h("div", { key: 'f6eebbdd589cf4eb506d3d94502a027e639889d9', class: 'col-12' }, h("form-control-select", { key: '112bb7ee88335d9b8e8f9300ff190f164b750b39', name: 'business_when_service_received', label: 'On average, how long after paying will your customers typically receive their goods or services?', inputHandler: this.inputHandler, errorText: (_c = this.errors) === null || _c === void 0 ? void 0 : _c.business_when_service_received, options: businessServiceReceivedOptions, defaultValue: additionalQuestionsDefaultValue === null || additionalQuestionsDefaultValue === void 0 ? void 0 : additionalQuestionsDefaultValue.business_when_service_received })), h("div", { key: '49864b5aeca86d221cc12690e2f9976fe9ee86ef', class: 'col-12' }, h("form-control-select", { key: 'a07159d8e7e49b9b1df5bcf2dcede994d0059595', name: 'business_recurring_payments', label: 'Does your business offer recurring payments?', inputHandler: this.inputHandler, errorText: (_d = this.errors) === null || _d === void 0 ? void 0 : _d.business_recurring_payments, options: recurringPaymentsOptions, defaultValue: additionalQuestionsDefaultValue === null || additionalQuestionsDefaultValue === void 0 ? void 0 : additionalQuestionsDefaultValue.business_recurring_payments })), h("div", { key: '3a03321d7dd9202c290443d756105bb2565a3e55', class: 'col-12' }, h("form-control-text", { key: '319a5fcb7ac6e975c41cfd5b19d5de0e98504f5d', name: 'business_recurring_payments_percentage', label: 'What percent of revenue is generated from each recurring payment type offered?', inputHandler: this.inputHandler, errorText: (_e = this.errors) === null || _e === void 0 ? void 0 : _e.business_recurring_payments_percentage, defaultValue: additionalQuestionsDefaultValue === null || additionalQuestionsDefaultValue === void 0 ? void 0 : additionalQuestionsDefaultValue.business_recurring_payments_percentage })), h("div", { key: 'e108f26d04e7f9510f89ab09daab53e86f5c30e1', class: 'col-12' }, h("form-control-select", { key: '45fb9296592415ddb947b55e3c7c73d198b96fac', name: 'business_seasonal', label: 'Is this business seasonal?', inputHandler: this.inputHandler, errorText: (_f = this.errors) === null || _f === void 0 ? void 0 : _f.business_seasonal, options: seasonalBusinessOptions, defaultValue: additionalQuestionsDefaultValue === null || additionalQuestionsDefaultValue === void 0 ? void 0 : additionalQuestionsDefaultValue.business_seasonal })), h("div", { key: '0b40ac8429c07279acb3d360124853a608a01664', class: 'col-12' }, h("form-control-text", { key: '880e00acdfa51f95b643f1ecc8d6fccba1167e6c', name: 'business_other_payment_details', label: 'Is there anything else you would like us to know about how your customers pay the business?', inputHandler: this.inputHandler, errorText: (_g = this.errors) === null || _g === void 0 ? void 0 : _g.business_other_payment_details, defaultValue: additionalQuestionsDefaultValue === null || additionalQuestionsDefaultValue === void 0 ? void 0 : additionalQuestionsDefaultValue.business_other_payment_details }))))));
    }
}, [0, "justifi-additional-questions-form-step-core", {
        "getBusiness": [16],
        "patchBusiness": [16],
        "allowOptionalFields": [4, "allow-optional-fields"],
        "formController": [32],
        "errors": [32],
        "additional_questions": [32],
        "validateAndSubmit": [64]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["justifi-additional-questions-form-step-core", "form-control-error-text", "form-control-help-text", "form-control-monetary", "form-control-select", "form-control-text"];
    components.forEach(tagName => { switch (tagName) {
        case "justifi-additional-questions-form-step-core":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, AdditionalQuestionsFormStepCore);
            }
            break;
        case "form-control-error-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "form-control-help-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "form-control-monetary":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "form-control-select":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "form-control-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { AdditionalQuestionsFormStepCore as A, defineCustomElement as d };
